import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ExportCondenseCriterion } from '../../model/export-condense-criterion.model';
import { environment } from '../../../environments/environment';

/** API shape of 'Export_CondenseCriterion' as returned/accepted by the BFF. */
interface ExportCondenseCriterionApiModel {
  exportCondenseCriterion_ID: string;
  exportDefinition_ID: string;
  number: number;
  changeUser?: string | null;
}

interface AddExportCondenseCriterionResponse {
  data?: string;
  exportCondenseCriterion_ID?: string;
  exportCondenseCriterionId?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Condense criteria behave as on/off bits: POST sets a number, DELETE resets it,
 * so the API intentionally has no PUT. Numbers 1 - 8 are stored in
 * 'Export_CondenseCriterion'; the reserved numbers -1 .. -3 map onto bit fields
 * of 'Export_Definition' but use the same POST / DELETE / ByContext calls.
 */
@Injectable({
  providedIn: 'root',
})
export class ExportCondenseCriterionService {
  private apiUrl =
    environment.apiUrl + '/kbj-frontend/api/v1/ExportDefinition/ExportCondenseCriterion';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all condense criteria of one export definition, ordered by number,
   * including the reserved -1 .. -3 entries whose bit is set (their id is derived
   * from the export definition id by the backend). Returns an empty array when
   * the API responds with 404 or 204 No Content.
   */
  getByExportDefinition(exportDefinitionId: string): Observable<ExportCondenseCriterion[]> {
    return this.http
      .get<ExportCondenseCriterionApiModel[] | null>(
        `${this.apiUrl}/ByContext/${exportDefinitionId}`,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) =>
          (jsonData ?? [])
            .map((item) => this.mapApiModelToModel(item))
            .sort((a, b) => a.number - b.number),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new condense criterion. The id is forced to the empty guid so the
   * backend generates a new one.
   */
  addCriterion(criterion: ExportCondenseCriterion): Observable<ExportCondenseCriterion> {
    const payload = this.mapModelToApiPayload(criterion, EMPTY_GUID);

    return this.http
      .post<AddExportCondenseCriterionResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({
          ...criterion,
          exportCondenseCriterionId: this.getCreatedId(response),
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Removes a condense criterion by its id.
   */
  removeCriterion(id: string): Observable<unknown> {
    return this.http
      .delete(`${this.apiUrl}/${id}`, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  // ---------------------------------------------------------------------------
  // Mapping helpers

  private mapApiModelToModel(api: ExportCondenseCriterionApiModel): ExportCondenseCriterion {
    return {
      exportCondenseCriterionId: api.exportCondenseCriterion_ID,
      exportDefinitionId: api.exportDefinition_ID,
      number: api.number,
    };
  }

  private mapModelToApiPayload(
    model: ExportCondenseCriterion,
    id: string,
  ): ExportCondenseCriterionApiModel {
    return {
      exportCondenseCriterion_ID: id,
      exportDefinition_ID: model.exportDefinitionId,
      number: model.number,
      changeUser: EMPTY_GUID,
    };
  }

  private getCreatedId(response: AddExportCondenseCriterionResponse | null): string {
    return (
      response?.data ??
      response?.exportCondenseCriterion_ID ??
      response?.exportCondenseCriterionId ??
      EMPTY_GUID
    );
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
